import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useOutletContext, Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

function parseSendDays(str) {
  if (!str) return [];
  // Backward-compat: legacy 'mon-fri' literal
  if (str === 'mon-fri') return ['mon','tue','wed','thu','fri'];
  return str.split(',').map(s => s.trim().toLowerCase()).filter(d => DAY_KEYS.includes(d));
}

function getTimezoneOptions() {
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch (_) {}
  // Fallback common set
  return [
    'UTC', 'Europe/London', 'Europe/Dublin', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Toronto', 'America/Sao_Paulo',
    'Asia/Dubai', 'Asia/Singapore', 'Asia/Hong_Kong', 'Asia/Tokyo', 'Asia/Kolkata',
    'Australia/Sydney', 'Pacific/Auckland', 'Africa/Lagos', 'Africa/Johannesburg',
  ];
}

function Section({ title, right, children }) {
  return (
    <div style={{ border: '1px solid #EAE6E1', borderRadius: 8, marginBottom: 16, background: 'white' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #F0EDE9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{title}</div>
        {right}
      </div>
      <div style={{ padding: 16 }}>{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, color: '#111' }}>{children}</div>
    </div>
  );
}

function Stat({ label, value, sublabel, accent }) {
  return (
    <div style={{ background: 'white', border: '1px solid #EAE6E1', borderRadius: 8, padding: '12px 16px', minWidth: 130 }}>
      <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: accent || '#111' }}>{value}</div>
      {sublabel && <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{sublabel}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const c = {
    draft: { bg: '#F0F0F0', fg: '#666' },
    scheduled: { bg: '#EEF2FF', fg: '#4338ca' },
    active: { bg: '#E8F5EC', fg: '#1a7f3c' },
    paused: { bg: '#FDF6E3', fg: '#a16207' },
    completed: { bg: '#E8E8E8', fg: '#333' },
    stopped: { bg: '#FBE9E9', fg: '#b91c1c' },
  }[status] || { bg: '#F0F0F0', fg: '#666' };
  return (
    <span style={{ background: c.bg, color: c.fg, padding: '3px 12px', borderRadius: 999, fontSize: 12, fontWeight: 500, textTransform: 'capitalize' }}>
      {status || 'unknown'}
    </span>
  );
}

function DayPicker({ value, onChange, disabled }) {
  const set = new Set(parseSendDays(value));
  const toggle = (day) => {
    const next = new Set(set);
    if (next.has(day)) next.delete(day); else next.add(day);
    const ordered = DAY_KEYS.filter(d => next.has(d));
    onChange(ordered.join(','));
  };
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {DAY_KEYS.map(d => {
        const on = set.has(d);
        return (
          <button
            type="button"
            key={d}
            disabled={disabled}
            onClick={() => !disabled && toggle(d)}
            style={{
              padding: '6px 10px',
              borderRadius: 5,
              border: on ? '1px solid #8B1A1A' : '1px solid #D4CFC9',
              background: on ? '#8B1A1A' : 'white',
              color: on ? 'white' : '#666',
              fontSize: 12,
              fontWeight: 500,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled && !on ? 0.5 : 1,
            }}
          >
            {DAY_LABELS[d]}
          </button>
        );
      })}
    </div>
  );
}

export default function CampaignDetail() {
  const params = useParams();
  const campaignId = params.campaignId || params.id;
  const navigate = useNavigate();
  const ctx = useOutletContext() || {};
  const { clientId, isGlobalMode } = ctx;
  const { getToken } = useAuth();

  const [campaign, setCampaign] = useState(null);
  const [client, setClient] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsQ, setContactsQ] = useState('');
  const [contactsStatus, setContactsStatus] = useState('');
  const [contactsOffset, setContactsOffset] = useState(0);
  const [contactsLimit] = useState(50);
  const [selectedCcIds, setSelectedCcIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Conversation modal state — opened by clicking a contact row
  const [convoOpenContactId, setConvoOpenContactId] = useState(null);
  const [convoData, setConvoData] = useState(null);
  const [convoLoading, setConvoLoading] = useState(false);
  const [campaignMailboxes, setCampaignMailboxes] = useState([]);
  const [mailboxPickerOpen, setMailboxPickerOpen] = useState(false);
  const [linkedPersonas, setLinkedPersonas] = useState([]);
  const [availablePersonas, setAvailablePersonas] = useState([]);
  const [personaToLink, setPersonaToLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  // editable config
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [timezone, setTimezone] = useState('');
  const [sendStart, setSendStart] = useState('09:00');
  const [sendEnd, setSendEnd] = useState('17:30');
  const [sendDays, setSendDays] = useState('mon,tue,wed,thu,fri');
  const [throttlePerHour, setThrottlePerHour] = useState(30);
  const [throttlePerDomain, setThrottlePerDomain] = useState(5);
  const [minGap, setMinGap] = useState(60);
  const [pauseOnReply, setPauseOnReply] = useState(true);
  const [pauseOnBounce, setPauseOnBounce] = useState(true);
  const [stepWaitDays, setStepWaitDays] = useState([2, 3, 4, 5]);

  const tzOptions = useMemo(() => getTimezoneOptions(), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}`, { headers });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const data = await res.json();
      setCampaign(data);
      setName(data.name || '');
      setDescription(data.description || '');
      setTimezone(data.timezone || 'Europe/London');
      setSendStart((data.send_window_start || '09:00:00').slice(0, 5));
      setSendEnd((data.send_window_end || '17:30:00').slice(0, 5));
      setSendDays(data.send_days || 'mon,tue,wed,thu,fri');
      setThrottlePerHour(data.throttle_per_hour ?? 30);
      setThrottlePerDomain(data.throttle_per_domain_per_hour ?? 5);
      setMinGap(data.min_gap_seconds ?? 60);
      setPauseOnReply(!!data.pause_on_reply);
      setPauseOnBounce(!!data.pause_on_bounce);
      setStepWaitDays(Array.isArray(data.default_step_wait_days) && data.default_step_wait_days.length ? data.default_step_wait_days : [2, 3, 4, 5]);

      if (data.client_id) {
        const cres = await fetch(`${API_BASE}/clients/${data.client_id}`, { headers });
        if (cres.ok) setClient(await cres.json());
      }

      // Initial contacts page (loadContacts below handles search/filter/pagination on demand)
      const ccres = await fetch(`${API_BASE}/campaigns/${campaignId}/contacts?limit=50&offset=0`, { headers });
      if (ccres.ok) {
        const cdata = await ccres.json();
        setContacts(cdata.rows || []);
        setContactsTotal(cdata.total || 0);
      }

      const mbres = await fetch(`${API_BASE}/campaigns/${campaignId}/mailboxes`, { headers });
      if (mbres.ok) setCampaignMailboxes(await mbres.json());

      const [lpRes, apRes] = await Promise.all([
        fetch(`${API_BASE}/campaigns/${campaignId}/personas`, { headers }),
        fetch(`${API_BASE}/clients/${clientId || data.client_id}/personas`, { headers }),
      ]);
      if (lpRes.ok) setLinkedPersonas(await lpRes.json());
      if (apRes.ok) setAvailablePersonas(await apRes.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [campaignId, getToken, clientId]);

  // Reload paginated/filtered contacts only (does not refetch the whole campaign).
  const loadContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const token = await getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams();
      params.set('limit', String(contactsLimit));
      params.set('offset', String(contactsOffset));
      if (contactsQ.trim()) params.set('q', contactsQ.trim());
      if (contactsStatus) params.set('status', contactsStatus);
      const r = await fetch(`${API_BASE}/campaigns/${campaignId}/contacts?${params.toString()}`, { headers });
      if (r.ok) {
        const data = await r.json();
        setContacts(data.rows || []);
        setContactsTotal(data.total || 0);
        // Drop any selections that no longer appear in the current page
        const visible = new Set((data.rows || []).map(r2 => r2.id));
        setSelectedCcIds(prev => new Set([...prev].filter(id => visible.has(id))));
      }
    } finally {
      setContactsLoading(false);
    }
  }, [campaignId, contactsLimit, contactsOffset, contactsQ, contactsStatus, getToken]);

  // Debounce search-driven reloads
  useEffect(() => {
    const t = setTimeout(() => { loadContacts(); }, 250);
    return () => clearTimeout(t);
  }, [loadContacts]);

  async function runBulk(action) {
    if (selectedCcIds.size === 0) return;
    if (action === 'remove' && !window.confirm(`Remove ${selectedCcIds.size} contact(s) from this campaign? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const token = await getToken();
      const headers = { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' };
      const r = await fetch(`${API_BASE}/campaigns/${campaignId}/contacts/bulk-action`, {
        method: 'POST', headers,
        body: JSON.stringify({ action, campaign_contact_ids: [...selectedCcIds] }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        alert('Bulk action failed: ' + (e.error || r.status));
      }
      setSelectedCcIds(new Set());
      await loadContacts();
      await load();
    } finally {
      setBulkBusy(false);
    }
  }

  function toggleSelectAll() {
    if (selectedCcIds.size === contacts.length && contacts.length > 0) setSelectedCcIds(new Set());
    else setSelectedCcIds(new Set(contacts.map(c => c.id)));
  }
  function toggleSelectOne(id) {
    setSelectedCcIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }

  // Load conversation when a contact row is clicked
  useEffect(() => {
    if (!convoOpenContactId) { setConvoData(null); return; }
    let cancelled = false;
    (async () => {
      setConvoLoading(true);
      try {
        const token = await getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await fetch(`${API_BASE}/conversations/by-contact/${convoOpenContactId}?campaign_id=${campaignId}`, { headers });
        if (r.ok) {
          const data = await r.json();
          if (!cancelled) setConvoData(data);
        }
      } finally {
        if (!cancelled) setConvoLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [convoOpenContactId, campaignId, getToken]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name, description, timezone,
          send_window_start: sendStart, send_window_end: sendEnd, send_days: sendDays,
          throttle_per_hour: throttlePerHour, throttle_per_domain_per_hour: throttlePerDomain,
          min_gap_seconds: minGap,
          pause_on_reply: pauseOnReply, pause_on_bounce: pauseOnBounce,
          default_step_wait_days: stepWaitDays,
        }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      setSavedMsg('Saved');
      setEditing(false);
      setTimeout(() => setSavedMsg(''), 2000);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const action = async (path, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}${path}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Action failed (${res.status})`);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const removeMailbox = async (mailboxId, emailLabel) => {
    if (!window.confirm(`Remove ${emailLabel} from this campaign? Pending sends from this mailbox will reassign to others.`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}/mailboxes/${mailboxId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Remove failed (${res.status})`);
      await load();
    } catch (e) { setError(e.message); }
  };

  const linkPersona = async () => {
    if (!personaToLink) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}/personas/${personaToLink}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setPersonaToLink('');
      await load();
    } catch (e) {
      setError('Could not link persona');
    }
  };

  const unlinkPersona = async (personaId, label) => {
    if (!window.confirm(`Unlink "${label}" from this campaign? Sending will fall back to the direct mailbox list below.`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}/personas/${personaId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      await load();
    } catch (e) {
      setError('Could not unlink persona');
    }
  };

  const toggleMailboxActive = async (mailboxId, currentActive) => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}/mailboxes/${mailboxId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !currentActive }),
      });
      if (!res.ok) throw new Error(`Toggle failed (${res.status})`);
      await load();
    } catch (e) { setError(e.message); }
  };

  const hardDelete = async () => {
    if (!window.confirm('PERMANENT: delete this campaign and all its contact state?')) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      navigate(isGlobalMode ? '/mail/campaigns' : `/clients/${clientId}/mail/campaigns`);
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading campaign…</div>;
  if (error) return <div style={{ padding: 24, color: '#b91c1c' }}>Error: {error}</div>;
  if (!campaign) return <div style={{ padding: 24 }}>Campaign not found.</div>;

  const stats = campaign.stats || {};
  const status = campaign.status;
  const isDraft = status === 'draft';
  const isActive = status === 'active' || status === 'scheduled';
  const isPaused = status === 'paused';
  const isTerminal = status === 'completed' || status === 'stopped';
  const canEdit = isDraft || isPaused;
  const completionPct = stats.total > 0 ? Math.round((Number(stats.completed || 0) / Number(stats.total || 1)) * 100) : 0;

  return (
    <div style={{ padding: 24, maxWidth: 1000 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>{campaign.name}</h2>
        <StatusBadge status={status} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#666', fontSize: 13, marginBottom: 20, flexWrap: 'wrap' }}>
        {campaign.pool_name && <><span>Pool: {campaign.pool_name}</span><span>·</span></>}
        {campaign.mailbox_email && <><span>Mailbox: {campaign.mailbox_email}</span><span>·</span></>}
        <span>Created {new Date(campaign.created_at).toLocaleDateString('en-GB')}</span>
        {campaign.started_at && (
          <><span>·</span><span>Started {new Date(campaign.started_at).toLocaleDateString('en-GB')}</span></>
        )}
        {client && isGlobalMode && (
          <>
            <span>·</span>
            <Link to={`/clients/${client.id}/mail/campaigns`} style={{ color: '#8B1A1A', textDecoration: 'none' }}>
              {client.name}
            </Link>
          </>
        )}
      </div>

      {savedMsg && (
        <div style={{ background: '#E8F5EC', color: '#1a7f3c', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 16 }}>
          {savedMsg}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {isDraft && <button onClick={() => action('/start', 'Launch this campaign?')} style={btnPrimary}>Launch</button>}
        {isActive && (
          <>
            <button onClick={() => action('/pause', 'Pause this campaign?')} style={btnSecondary}>Pause</button>
            <button onClick={() => action('/stop', 'Stop this campaign? This cannot be undone.')} style={btnDanger}>Stop</button>
          </>
        )}
        {isPaused && (
          <>
            <button onClick={() => action('/resume', 'Resume this campaign?')} style={btnPrimary}>Resume</button>
            <button onClick={() => action('/stop', 'Stop this campaign? This cannot be undone.')} style={btnDanger}>Stop</button>
          </>
        )}
        {isTerminal && (
          <span style={{ fontSize: 13, color: '#888' }}>Campaign {status}. No further actions.</span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <Stat label="Total" value={stats.total ?? 0} />
        <Stat label="Sent" value={campaign.sent_count ?? 0} sublabel={`${completionPct}% complete`} accent="#1a7f3c" />
        <Stat label="Pending" value={stats.pending ?? 0} />
        <Stat label="In progress" value={stats.in_progress ?? 0} />
        <Stat label="Replied" value={stats.replied ?? 0} accent="#1a56a0" />
        <Stat label="Bounced" value={stats.bounced ?? 0} accent="#b91c1c" />
      </div>

      <Section
        title={`Contacts (${contactsTotal})`}
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              value={contactsQ}
              onChange={e => { setContactsOffset(0); setContactsQ(e.target.value); }}
              placeholder="Search name, email, company..."
              style={{ fontSize: 13, border: '1px solid #ddd', borderRadius: 6, padding: '5px 10px', minWidth: 240 }}
            />
            <select
              value={contactsStatus}
              onChange={e => { setContactsOffset(0); setContactsStatus(e.target.value); }}
              style={{ fontSize: 13, border: '1px solid #ddd', borderRadius: 6, padding: '5px 8px' }}
            >
              <option value="">All status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In progress</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
              <option value="replied">Replied</option>
              <option value="bounced">Bounced</option>
            </select>
          </div>
        }
      >
        {selectedCcIds.size > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, padding: '8px 12px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, fontSize: 13 }}>
            <span style={{ fontWeight: 500 }}>{selectedCcIds.size} selected</span>
            <span style={{ color: '#999' }}>·</span>
            <button onClick={() => runBulk('pause')} disabled={bulkBusy} style={btnSecondarySm}>Pause</button>
            <button onClick={() => runBulk('resume')} disabled={bulkBusy} style={btnSecondarySm}>Resume</button>
            <button onClick={() => runBulk('send-now')} disabled={bulkBusy} style={btnSecondarySm}>Send now</button>
            <button onClick={() => runBulk('skip-step')} disabled={bulkBusy} style={btnSecondarySm}>Skip step</button>
            <button onClick={() => runBulk('remove')} disabled={bulkBusy} style={btnDangerSolid}>Remove</button>
            <span style={{ flex: 1 }} />
            <button onClick={() => setSelectedCcIds(new Set())} style={{ ...btnSecondarySm, color: '#666' }}>Clear</button>
          </div>
        )}
        {contactsLoading && contacts.length === 0 ? (
          <div style={{ color: '#888', fontSize: 13, padding: '24px 0' }}>Loading...</div>
        ) : contactsTotal === 0 ? (
          <div style={{ color: '#888', fontSize: 13 }}>
            {contactsQ || contactsStatus ? 'No contacts match the current filter.' : 'No contacts enrolled yet.'}
          </div>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #eee', color: '#666' }}>
                  <th style={{ padding: '8px 10px', width: 28 }}>
                    <input
                      type="checkbox"
                      checked={selectedCcIds.size === contacts.length && contacts.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th style={{ padding: '8px 10px', fontWeight: 500 }}>Contact</th>
                  <th style={{ padding: '8px 10px', fontWeight: 500 }}>Status</th>
                  <th style={{ padding: '8px 10px', fontWeight: 500 }}>Step</th>
                  <th style={{ padding: '8px 10px', fontWeight: 500 }}>Next send</th>
                  <th style={{ padding: '8px 10px', fontWeight: 500 }}>Last event</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(cc => (
                  <tr
                    key={cc.id}
                    onClick={() => setConvoOpenContactId(cc.contact_id)}
                    style={{
                      borderBottom: '1px solid #f5f5f5',
                      background: selectedCcIds.has(cc.id) ? '#FFFBEB' : 'transparent',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={e => { if (!selectedCcIds.has(cc.id)) e.currentTarget.style.background = '#FAFAFA'; }}
                    onMouseLeave={e => { if (!selectedCcIds.has(cc.id)) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td style={{ padding: '8px 10px' }} onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedCcIds.has(cc.id)}
                        onChange={() => toggleSelectOne(cc.id)}
                      />
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ fontWeight: 500 }}>{cc.full_name || `Contact ${cc.contact_id}`}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>{cc.email}{cc.company_name ? ` · ${cc.company_name}` : ''}</div>
                    </td>
                    <td style={{ padding: '8px 10px' }}><StatusBadge status={cc.status} /></td>
                    <td style={{ padding: '8px 10px' }}>{cc.current_step ?? 0}</td>
                    <td style={{ padding: '8px 10px', color: '#666' }}>
                      {cc.next_send_at ? new Date(cc.next_send_at).toLocaleString('en-GB') : '—'}
                    </td>
                    <td style={{ padding: '8px 10px', color: '#666' }}>
                      {cc.last_event || '—'}{cc.last_event_at ? ` · ${new Date(cc.last_event_at).toLocaleDateString('en-GB')}` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, fontSize: 13, color: '#666' }}>
              <div>
                Showing {contactsTotal === 0 ? 0 : contactsOffset + 1}-{Math.min(contactsOffset + contacts.length, contactsTotal)} of {contactsTotal}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setContactsOffset(Math.max(0, contactsOffset - contactsLimit))}
                  disabled={contactsOffset === 0 || contactsLoading}
                  style={btnSecondarySm}
                >Previous</button>
                <button
                  onClick={() => setContactsOffset(contactsOffset + contactsLimit)}
                  disabled={contactsOffset + contactsLimit >= contactsTotal || contactsLoading}
                  style={btnSecondarySm}
                >Next</button>
              </div>
            </div>
          </>
        )}
      </Section>

      <Section
        title="Configuration"
        right={
          canEdit ? (
            editing ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={save} style={btnPrimarySm}>Save</button>
                <button onClick={() => { setEditing(false); load(); }} style={btnSecondarySm}>Cancel</button>
              </div>
            ) : (
              <button onClick={() => setEditing(true)} style={btnSecondarySm}>Edit</button>
            )
          ) : (
            <span style={{ fontSize: 12, color: '#999' }}>Read-only ({status})</span>
          )
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <Field label="Name">
            {editing ? <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} /> : campaign.name}
          </Field>
          <Field label="Timezone">
            {editing ? (
              <select value={timezone} onChange={(e) => setTimezone(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
                {tzOptions.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            ) : campaign.timezone}
          </Field>
          <Field label="Send window">
            {editing ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="time" value={sendStart} onChange={(e) => setSendStart(e.target.value)} style={inputStyle} />
                <span>to</span>
                <input type="time" value={sendEnd} onChange={(e) => setSendEnd(e.target.value)} style={inputStyle} />
              </span>
            ) : `${sendStart} – ${sendEnd}`}
          </Field>
          <Field label="Throttle / hour">
            {editing ? <input type="number" value={throttlePerHour} onChange={(e) => setThrottlePerHour(parseInt(e.target.value) || 0)} style={inputStyle} /> : campaign.throttle_per_hour}
          </Field>
          <Field label="Throttle / domain / hour">
            {editing ? <input type="number" value={throttlePerDomain} onChange={(e) => setThrottlePerDomain(parseInt(e.target.value) || 0)} style={inputStyle} /> : campaign.throttle_per_domain_per_hour}
          </Field>
          <Field label="Min gap (seconds)">
            {editing ? <input type="number" value={minGap} onChange={(e) => setMinGap(parseInt(e.target.value) || 0)} style={inputStyle} /> : campaign.min_gap_seconds}
          </Field>
          <Field label="Description">
            {editing ? <input value={description} onChange={(e) => setDescription(e.target.value)} style={inputStyle} /> : (campaign.description || '—')}
          </Field>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Step delays (days between steps)</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {stepWaitDays.map((d, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#555' }}>
                Step {i + 1} → {i + 2}:
                {editing ? (
                  <input
                    type="number"
                    min={0}
                    max={90}
                    value={d}
                    onChange={(e) => {
                      const v = Math.max(0, Math.min(90, parseInt(e.target.value) || 0));
                      setStepWaitDays(prev => prev.map((x, idx) => idx === i ? v : x));
                    }}
                    style={{ ...inputStyle, width: 56 }}
                  />
                ) : <strong style={{ color: '#222' }}>{d}</strong>}
                <span style={{ color: '#888' }}>d</span>
              </span>
            ))}
            {editing && (
              <>
                <button type="button"
                  onClick={() => setStepWaitDays(prev => [...prev, prev[prev.length - 1] || 3])}
                  style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #d4d4d4', borderRadius: 3, background: '#fff', cursor: 'pointer', color: '#333' }}>
                  + Add gap
                </button>
                {stepWaitDays.length > 1 && (
                  <button type="button"
                    onClick={() => setStepWaitDays(prev => prev.slice(0, -1))}
                    style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #d4d4d4', borderRadius: 3, background: '#fff', cursor: 'pointer', color: '#666' }}>
                    − Remove last
                  </button>
                )}
              </>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>Applies to every contact in this campaign unless a sequence step overrides it.</div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Send days</div>
          <DayPicker value={sendDays} onChange={setSendDays} disabled={!editing} />
        </div>

        <div style={{ display: 'flex', gap: 24, fontSize: 13 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: editing ? 'pointer' : 'default' }}>
            <input type="checkbox" checked={pauseOnReply} onChange={(e) => setPauseOnReply(e.target.checked)} disabled={!editing} />
            Pause contact on reply
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: editing ? 'pointer' : 'default' }}>
            <input type="checkbox" checked={pauseOnBounce} onChange={(e) => setPauseOnBounce(e.target.checked)} disabled={!editing} />
            Pause contact on bounce
          </label>
        </div>
      </Section>

      <Section
        title={`Sending persona${linkedPersonas.length === 1 ? '' : 's'}${linkedPersonas.length ? ` (${linkedPersonas.length})` : ''}`}
      >
        {linkedPersonas.length > 0 ? (
          <>
            <div style={{ background: '#F0F7F0', border: '1px solid #BFE3BF', color: '#1a5c1a', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>
              Sending routes through the linked persona. The engine rotates across the persona's backing mailboxes; the direct mailbox list below is bypassed while a persona is linked.
            </div>
            {linkedPersonas.map(p => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', border: '1px solid #eee', borderRadius: 8, marginBottom: 8 }}>
                <div>
                  <Link to={isGlobalMode ? `/mail/personas` : `/clients/${clientId}/mail/personas`}
                        style={{ color: '#8B1A1A', textDecoration: 'none', fontWeight: 600, fontSize: 14 }}>
                    {p.display_name}
                  </Link>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                    {p.job_title ? `${p.job_title} · ` : ''}{p.mailbox_count} backing mailbox{p.mailbox_count === '1' ? '' : 'es'}
                    {Number(p.mailbox_count) === 0 && <span style={{ color: '#b91c1c', marginLeft: 6 }}>— no mailboxes, will not send</span>}
                  </div>
                </div>
                <button onClick={() => unlinkPersona(p.id, p.display_name)} style={{ background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', fontSize: 12, padding: 0 }}>
                  Unlink
                </button>
              </div>
            ))}
          </>
        ) : (
          <div style={{ color: '#888', fontSize: 13, padding: '4px 0 12px' }}>
            No persona linked. Sending uses the mailbox list below. Link a persona to send under one identity across rotating mailboxes.
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <select value={personaToLink} onChange={e => setPersonaToLink(e.target.value)}
            style={{ flex: 1, maxWidth: 320, padding: '6px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}>
            <option value="">Select a persona to link…</option>
            {availablePersonas.filter(p => !new Set(linkedPersonas.map(p => p.id)).has(p.id)).map(p => (
              <option key={p.id} value={p.id}>{p.display_name} ({p.mailbox_count} mailbox{p.mailbox_count === '1' ? '' : 'es'})</option>
            ))}
          </select>
          <button onClick={linkPersona} disabled={!personaToLink} style={personaToLink ? btnPrimarySm : { ...btnPrimarySm, background: '#CCC', cursor: 'default' }}>
            Link persona
          </button>
        </div>
        {availablePersonas.length === 0 && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
            No personas for this client yet. Create one under <Link to={isGlobalMode ? `/mail/personas` : `/clients/${clientId}/mail/personas`} style={{ color: '#8B1A1A' }}>Personas</Link>.
          </div>
        )}
      </Section>

      <Section
        title={`Sending mailboxes (${campaignMailboxes.filter(m => m.active).length} active / ${campaignMailboxes.length} total)`}
        right={
          <button onClick={() => setMailboxPickerOpen(true)} style={btnPrimarySm}>+ Add mailboxes</button>
        }
      >
        {isActive && (
          <div style={{ background: '#FFF8E1', border: '1px solid #FFE08A', color: '#7A5C00', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>
            Campaign is active. Removing a mailbox will redistribute its remaining sends to others. New mailboxes added now will start rotating immediately.
          </div>
        )}
        {campaignMailboxes.length === 0 ? (
          <div style={{ color: '#888', fontSize: 13, padding: 8 }}>No mailboxes attached. Add at least one to send from.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #eee', color: '#666' }}>
                <th style={{ padding: '8px 10px', fontWeight: 500 }}>Email</th>
                <th style={{ padding: '8px 10px', fontWeight: 500 }}>Provider</th>
                <th style={{ padding: '8px 10px', fontWeight: 500 }}>Owner</th>
                <th style={{ padding: '8px 10px', fontWeight: 500 }}>Status</th>
                <th style={{ padding: '8px 10px', fontWeight: 500 }}>Daily cap</th>
                <th style={{ padding: '8px 10px', fontWeight: 500 }}>Sent today</th>
                <th style={{ padding: '8px 10px', fontWeight: 500 }}>In this campaign</th>
                <th style={{ padding: '8px 10px', fontWeight: 500 }}></th>
              </tr>
            </thead>
            <tbody>
              {campaignMailboxes.map(m => (
                <tr key={m.id} style={{ borderBottom: '1px solid #f5f5f5', opacity: m.active ? 1 : 0.55 }}>
                  <td style={{ padding: '8px 10px' }}>
                    <Link to={isGlobalMode ? `/mail/mailboxes/${m.mailbox_id}` : `/clients/${clientId}/mail/mailboxes/${m.mailbox_id}`}
                          style={{ color: '#8B1A1A', textDecoration: 'none' }}>
                      {m.email_address}
                    </Link>
                    {m.display_name && <span style={{ color: '#888', marginLeft: 6 }}>— {m.display_name}</span>}
                  </td>
                  <td style={{ padding: '8px 10px', textTransform: 'capitalize' }}>{m.provider}</td>
                  <td style={{ padding: '8px 10px', textTransform: 'capitalize' }}>{m.owner_type || 'client'}</td>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{
                      color: m.status === 'active' ? '#1a7f3c' : m.status === 'error' ? '#b91c1c' : '#888',
                      fontWeight: 500,
                    }}>{m.status || 'unknown'}</span>
                  </td>
                  <td style={{ padding: '8px 10px' }}>{m.daily_cap ?? '—'}</td>
                  <td style={{ padding: '8px 10px' }}>{m.sent_today ?? 0}</td>
                  <td style={{ padding: '8px 10px' }}>
                    <button onClick={() => toggleMailboxActive(m.mailbox_id, m.active)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: m.active ? '#1a7f3c' : '#888', fontSize: 12, fontWeight: 500, padding: 0 }}>
                      {m.active ? 'Active' : 'Paused'}
                    </button>
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                    <button onClick={() => removeMailbox(m.mailbox_id, m.email_address)} style={{ background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', fontSize: 12, padding: 0 }}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ marginTop: 10, fontSize: 12, color: '#888' }}>
          Click "In this campaign" status to pause/resume an individual mailbox without removing it. Sends rotate across all active mailboxes.
        </div>
      </Section>

      <Section title="Danger zone">
        <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
          Permanently delete this campaign and all its contact state. This cannot be undone.
        </div>
        <button onClick={hardDelete} style={btnDangerSolid}>Delete campaign</button>
      </Section>
      {mailboxPickerOpen && (
        <AddMailboxesModal
          clientId={clientId || campaign.client_id}
          campaignId={campaignId}
          existingMailboxIds={new Set(campaignMailboxes.map(m => m.mailbox_id))}
          onClose={() => setMailboxPickerOpen(false)}
          onAdded={async () => {
            setMailboxPickerOpen(false);
            await load();
          }}
        />
      )}

      {convoOpenContactId && (
        <div
          onClick={() => setConvoOpenContactId(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '60px 20px', overflowY: 'auto' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 10, width: '100%', maxWidth: 820, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}
          >
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #EEE', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                {convoData && convoData.contact ? (
                  <>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>{convoData.contact.full_name || convoData.contact.email}</div>
                    <div style={{ fontSize: 12, color: '#888' }}>
                      {convoData.contact.email}
                      {convoData.contact.job_title ? ` · ${convoData.contact.job_title}` : ''}
                      {convoData.contact.company_name ? ` · ${convoData.contact.company_name}` : ''}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 16, fontWeight: 600 }}>Conversation</div>
                )}
              </div>
              <button onClick={() => setConvoOpenContactId(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: 20, maxHeight: '65vh', overflowY: 'auto' }}>
              {convoLoading ? (
                <div style={{ color: '#888', fontSize: 13 }}>Loading conversation…</div>
              ) : !convoData || !convoData.messages || convoData.messages.length === 0 ? (
                <div style={{ color: '#888', fontSize: 13 }}>No messages yet for this contact in this campaign.</div>
              ) : (
                convoData.messages.map(m => (
                  <div
                    key={`${m.direction}-${m.id}`}
                    style={{
                      marginBottom: 16,
                      padding: 14,
                      borderRadius: 8,
                      background: m.direction === 'outbound' ? '#F4F1EE' : '#EEF6FF',
                      border: `1px solid ${m.direction === 'outbound' ? '#E5DFD7' : '#D4E4F7'}`,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#666', marginBottom: 6 }}>
                      <div>
                        <strong style={{ color: m.direction === 'outbound' ? '#8B1A1A' : '#1A56A0' }}>
                          {m.direction === 'outbound' ? 'Sent' : 'Received'}
                        </strong>
                        {m.step_number != null ? ` · Step ${m.step_number}` : ''}
                        {m.status ? ` · ${m.status}` : ''}
                      </div>
                      <div>{m.occurred_at ? new Date(m.occurred_at).toLocaleString('en-GB') : ''}</div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>{m.subject || '(no subject)'}</div>
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
                      {m.direction === 'outbound' ? `To ${m.to_email}` : `From ${m.from_email}`}
                    </div>
                    <div
                      style={{ fontSize: 13, lineHeight: 1.55, color: '#333', whiteSpace: 'pre-wrap' }}
                      dangerouslySetInnerHTML={{ __html: m.body_html || (m.body_text || '').replace(/\n/g, '<br>') }}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function AddMailboxesModal({ clientId, campaignId, existingMailboxIds, onClose, onAdded }) {
  const { getToken } = useAuth();
  const [mailboxes, setMailboxes] = useState([]);
  const [pools, setPools] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const headers = { Authorization: `Bearer ${token}` };
        const [pRes, mRes] = await Promise.all([
          fetch(`${API_BASE}/clients/${clientId}/pools`, { headers }),
          fetch(`${API_BASE}/clients/${clientId}/mailboxes`, { headers }),
        ]);
        if (pRes.ok) setPools(await pRes.json());
        if (mRes.ok) {
          const data = await mRes.json();
          const list = Array.isArray(data) ? data : data.mailboxes || [];
          setMailboxes(list);
        }
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [clientId, getToken]);

  const loadPool = async (poolId) => {
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/pools/${poolId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const data = await r.json();
      const ids = (data.members || data.mailboxes || []).map(m => m.mailbox_id || m.id).filter(id => !existingMailboxIds.has(id));
      setSelected(new Set([...selected, ...ids]));
    } catch (_) {}
  };

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const available = mailboxes.filter(m => !existingMailboxIds.has(m.id));
  const filtered = available.filter(m => {
    if (providerFilter !== 'all' && m.provider !== providerFilter) return false;
    if (query) {
      const q = query.toLowerCase();
      if (!(m.email_address || '').toLowerCase().includes(q) &&
          !(m.display_name || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const providers = Array.from(new Set(available.map(m => m.provider).filter(Boolean)));

  const add = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}/mailboxes`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ mailbox_ids: Array.from(selected) }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Add failed (${res.status})`);
      }
      onAdded();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 };
  const dialog = { background: 'white', borderRadius: 10, padding: 24, width: 600, maxWidth: '92vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' };
  const inp = { padding: '8px 10px', border: '1px solid #D4CFC9', borderRadius: 5, fontSize: 13, boxSizing: 'border-box' };
  const statusColour = (s) => s === 'active' ? '#1a7f3c' : s === 'error' ? '#b91c1c' : '#888';

  return (
    <div style={overlay} onClick={onClose}>
      <div style={dialog} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 14px', fontSize: 18, fontWeight: 600 }}>Add mailboxes to campaign</h3>
        {loading ? (
          <div style={{ color: '#888' }}>Loading…</div>
        ) : (
          <>
            <PoolPicker pools={pools} onPick={loadPool} />

            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <input type="text" placeholder="Search mailboxes…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ ...inp, flex: 1 }} />
              {providers.length > 1 && (
                <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)} style={{ ...inp, width: 130 }}>
                  <option value="all">All providers</option>
                  {providers.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              )}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #EAE6E1', borderRadius: 6, minHeight: 200 }}>
              {available.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 13 }}>
                  All available mailboxes are already attached.
                </div>
              ) : filtered.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 13 }}>No mailboxes match.</div>
              ) : filtered.map(m => {
                const checked = selected.has(m.id);
                const disabled = m.status === 'disconnected';
                return (
                  <label key={m.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                    borderBottom: '1px solid #F5F2EE', cursor: disabled ? 'not-allowed' : 'pointer',
                    background: checked ? '#FBF1F1' : 'white', opacity: disabled ? 0.5 : 1,
                  }}>
                    <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggle(m.id)} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: '#111' }}>
                        {m.email_address}
                        {m.display_name && <span style={{ color: '#888', marginLeft: 6 }}>— {m.display_name}</span>}
                      </div>
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2, display: 'flex', gap: 8 }}>
                        <span style={{ textTransform: 'capitalize' }}>{m.provider || '—'}</span>
                        <span>·</span>
                        <span style={{ color: statusColour(m.status) }}>{m.status || 'unknown'}</span>
                        <span>·</span>
                        <span>cap {m.daily_cap ?? '—'}</span>
                        {m.owner_type === 'webcite' && <><span>·</span><span style={{ color: '#8B1A1A' }}>shared</span></>}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            {error && <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 10 }}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 14 }}>
              <div style={{ fontSize: 12, color: '#888' }}>{selected.size} selected</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={onClose} disabled={saving} style={btnSecondary}>Cancel</button>
                <button onClick={add} disabled={saving || selected.size === 0} style={{ ...btnPrimary, opacity: selected.size === 0 ? 0.5 : 1 }}>
                  {saving ? 'Adding…' : `Add ${selected.size > 0 ? selected.size : ''}`}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// Deterministic pastel from pool id — same pool always same colour
const POOL_PALETTE = [
  { bg: '#FBF1F1', border: '#E6C8C8', fg: '#8B1A1A' },
  { bg: '#EEF2FF', border: '#C7D2FE', fg: '#4338ca' },
  { bg: '#E8F5EC', border: '#BBE5C5', fg: '#1a7f3c' },
  { bg: '#FDF6E3', border: '#F5E0A8', fg: '#a16207' },
  { bg: '#FBE9E9', border: '#F2C5C5', fg: '#b91c1c' },
  { bg: '#F0F0F0', border: '#D4D4D4', fg: '#555' },
  { bg: '#F5E6FF', border: '#DDC3F0', fg: '#6B21A8' },
  { bg: '#E0F2F1', border: '#A7DCD9', fg: '#0F766E' },
  { bg: '#FFF1E6', border: '#F5D5B8', fg: '#9A4F0F' },
  { bg: '#E6F4FF', border: '#B8DBF5', fg: '#0369A1' },
];
function poolColour(id) {
  const n = typeof id === 'number' ? id : parseInt(id) || 0;
  return POOL_PALETTE[n % POOL_PALETTE.length];
}

function PoolPicker({ pools, onPick }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  if (!pools || pools.length === 0) return null;

  // Inline pills when <= 3 pools
  if (pools.length <= 3) {
    return (
      <div style={{ marginBottom: 12, padding: 10, background: '#FBF9F7', borderRadius: 6, border: '1px solid #EAE6E1' }}>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Quick add from pool:</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {pools.map(p => {
            const c = poolColour(p.id);
            return (
              <button key={p.id} type="button" onClick={() => onPick(p.id)}
                style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 5, padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: c.fg, fontWeight: 500 }}>
                + {p.name}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Searchable dropdown when > 3 pools
  const filtered = pools.filter(p => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (p.name || '').toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q);
  });

  return (
    <div style={{ marginBottom: 12, position: 'relative' }}>
      <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Quick add from pool ({pools.length} available):</div>
      <input
        type="text"
        placeholder="Search pools by name…"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={{ width: '100%', padding: '8px 10px', border: '1px solid #D4CFC9', borderRadius: 5, fontSize: 13, boxSizing: 'border-box' }}
      />
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0,
          background: 'white', border: '1px solid #EAE6E1', borderRadius: 6,
          boxShadow: '0 6px 20px rgba(0,0,0,0.08)', zIndex: 60,
          maxHeight: 280, overflowY: 'auto', padding: 4,
        }}>
          {filtered.slice(0, 50).map(p => {
            const c = poolColour(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onPick(p.id); setQuery(''); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  width: '100%', padding: '8px 10px', textAlign: 'left',
                  background: 'transparent', border: 'none', borderRadius: 5,
                  cursor: 'pointer', fontSize: 13,
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#FAFAFA'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.fg, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, flexShrink: 0 }}>
                  {p.name}
                </span>
                <span style={{ color: '#888', fontSize: 11 }}>
                  {p.members?.length ?? p.member_count ?? 0} mailbox{(p.members?.length ?? p.member_count ?? 0) !== 1 ? 'es' : ''}
                </span>
              </button>
            );
          })}
          {filtered.length > 50 && (
            <div style={{ padding: 8, fontSize: 11, color: '#888', textAlign: 'center' }}>Showing first 50 of {filtered.length}. Refine search.</div>
          )}
        </div>
      )}
      {open && filtered.length === 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0,
          background: 'white', border: '1px solid #EAE6E1', borderRadius: 6,
          padding: 12, fontSize: 13, color: '#888', zIndex: 60,
        }}>No pools match.</div>
      )}
    </div>
  );
}

const inputStyle = { padding: '6px 10px', border: '1px solid #D4CFC9', borderRadius: 5, fontSize: 13, boxSizing: 'border-box' };
const btnPrimary = { background: '#8B1A1A', color: 'white', border: 'none', borderRadius: 5, padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnSecondary = { background: 'white', color: '#333', border: '1px solid #D4CFC9', borderRadius: 5, padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnDanger = { background: 'white', color: '#b91c1c', border: '1px solid #b91c1c', borderRadius: 5, padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnDangerSolid = { background: '#b91c1c', color: 'white', border: 'none', borderRadius: 5, padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnPrimarySm = { ...btnPrimary, padding: '5px 10px', fontSize: 12 };
const btnSecondarySm = { ...btnSecondary, padding: '5px 10px', fontSize: 12 };
